import { ReactNode, useLayoutEffect, useRef } from 'react';
import composeRefs from '@seznam/compose-react-refs';
import { motion } from 'motion/react';
import { onlyText } from 'react-children-utilities';
import type { CommonHTMLButtonAttributes } from '@frontend/types';
import { createKeydownHandler, scrollIntoView } from '../../helpers';
import { useTooltip } from '../tooltip';
import { Tab } from './atoms';
import { useActiveTab, useTabSetter } from './tabs.provider';

type Props = CommonHTMLButtonAttributes & {
  children: ReactNode;
  /**
   * This field is deprecated and will be removed in future versions.
   * @deprecated Deriving this controls value now from the `id` prop.
   */
  controls?: string;
  disabled?: boolean;
  id: string;
  trackingId?: string;
  hoverLabel?: string;
};

export const TabListItem = ({ children, controls, id, onKeyDown, hoverLabel, ...rest }: Props) => {
  const tabRef = useRef<HTMLButtonElement | null>(null);
  const handleTabChange = useTabSetter();
  const { activeTab } = useActiveTab();
  const isActive = activeTab === id;
  const handleClick = () => {
    handleTabChange(id);
  };

  useLayoutEffect(() => {
    if (isActive && tabRef?.current?.disabled) {
      handleTabChange('');
    }
  }, [isActive]);

  const {
    Tooltip,
    triggerProps: { ref: tooltipRef, ...triggerProps },
    tooltipProps,
  } = useTooltip();

  return (
    <>
      {!!hoverLabel && <Tooltip {...tooltipProps}>{hoverLabel}</Tooltip>}
      <Tab
        {...rest}
        data-text={onlyText(children as React.ReactNode)}
        onKeyDown={createKeydownHandler({
          shouldLoop: true,
          shouldScrollIntoView: true,
          siblingSelector: '[role="tab"]',
          parentSelector: '[role="tablist"]',
          orientation: 'horizontal',
          onKeyDown,
        })}
        id={id}
        aria-controls={controls ?? `${id}-panel`}
        aria-selected={isActive}
        role='tab'
        tabIndex={isActive ? 0 : -1}
        onClick={() => {
          handleClick();
          scrollIntoView(tabRef.current, true);
        }}
        isActive={isActive}
        ref={composeRefs<HTMLButtonElement>(tabRef, tooltipRef)}
        {...(!!hoverLabel ? triggerProps : {})}
      >
        {children}
        {isActive && (
          <motion.span
            className='sliding__border'
            layoutId='sliding__border'
            initial={false}
            transition={{ ease: 'easeInOut', duration: 0.3 }}
          />
        )}
      </Tab>
    </>
  );
};
